import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../services/api';
import { useCondominio } from '../contexts/CondominioContext';
import { useAuth } from '../contexts/AuthContext';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  Download,
  Upload,
  FileText,
  File,
  Image,
  FileSpreadsheet
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';

const Documentos = () => {
  const { getCondominioId, isAdministrador } = useCondominio();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const [showModal, setShowModal] = useState(false);
  const [editingDocumento, setEditingDocumento] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    tipo: '',
    categoria: '',
    publico: ''
  });

  // Buscar documentos
  const { data: documentosData, isLoading } = useQuery(
    ['documentos', getCondominioId(), searchTerm, filters],
    () => api.get('/documentos', {
      params: {
        search: searchTerm,
        ...filters
      }
    }).then(res => res.data.data),
    {
      enabled: !!getCondominioId()
    }
  );

  // Criar/atualizar documento
  const documentoMutation = useMutation(
    (documentoData) => {
      if (editingDocumento) {
        return api.put(`/documentos/${editingDocumento.documento_id}`, documentoData);
      } else {
        const formData = new FormData();
        Object.keys(documentoData).forEach(key => {
          if (key === 'arquivo') {
            formData.append(key, documentoData[key]);
          } else {
            formData.append(key, documentoData[key]);
          }
        });
        return api.post('/documentos', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['documentos']);
        setShowModal(false);
        setEditingDocumento(null);
      }
    }
  );

  // Excluir documento
  const deleteMutation = useMutation(
    (id) => api.delete(`/documentos/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['documentos']);
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const documentoData = Object.fromEntries(formData.entries());
    
    // Converter campos booleanos
    documentoData.documento_publico = documentoData.documento_publico === 'on';
    
    documentoMutation.mutate(documentoData);
  };

  const handleEdit = (documento) => {
    setEditingDocumento(documento);
    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Tem certeza que deseja excluir este documento?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleDownload = async (id) => {
    try {
      const response = await api.get(`/documentos/${id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', response.headers['content-disposition']?.split('filename=')[1] || 'documento');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Erro ao fazer download:', error);
    }
  };

  const resetForm = () => {
    setEditingDocumento(null);
    setShowModal(false);
  };

  const getTipoIcon = (tipo) => {
    const icons = {
      regulamento: '📋',
      ata: '📝',
      contrato: '📄',
      manual: '📖',
      formulario: '📝',
      outros: '📁'
    };
    return icons[tipo] || '📁';
  };

  const getFormatoIcon = (formato) => {
    const icons = {
      pdf: <FileText className="h-4 w-4 text-red-600" />,
      doc: <FileText className="h-4 w-4 text-blue-600" />,
      docx: <FileText className="h-4 w-4 text-blue-600" />,
      xls: <FileSpreadsheet className="h-4 w-4 text-green-600" />,
      xlsx: <FileSpreadsheet className="h-4 w-4 text-green-600" />,
      jpg: <Image className="h-4 w-4 text-orange-600" />,
      jpeg: <Image className="h-4 w-4 text-orange-600" />,
      png: <Image className="h-4 w-4 text-orange-600" />
    };
    return icons[formato?.toLowerCase()] || <File className="h-4 w-4 text-gray-600" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <FileText className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Documentos</h1>
            <p className="text-sm text-gray-500">Gerencie os documentos do condomínio</p>
          </div>
        </div>
        {isAdministrador() && (
          <button
            onClick={() => setShowModal(true)}
            className="btn btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Adicionar Documento
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buscar
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Título ou descrição..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input-field pl-10"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo
              </label>
              <select
                value={filters.tipo}
                onChange={(e) => setFilters({...filters, tipo: e.target.value})}
                className="input-field"
              >
                <option value="">Todos</option>
                <option value="regulamento">Regulamento</option>
                <option value="ata">Ata</option>
                <option value="contrato">Contrato</option>
                <option value="manual">Manual</option>
                <option value="formulario">Formulário</option>
                <option value="outros">Outros</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categoria
              </label>
              <select
                value={filters.categoria}
                onChange={(e) => setFilters({...filters, categoria: e.target.value})}
                className="input-field"
              >
                <option value="">Todas</option>
                <option value="administrativo">Administrativo</option>
                <option value="financeiro">Financeiro</option>
                <option value="juridico">Jurídico</option>
                <option value="tecnico">Técnico</option>
                <option value="social">Social</option>
                <option value="outros">Outros</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Visibilidade
              </label>
              <select
                value={filters.publico}
                onChange={(e) => setFilters({...filters, publico: e.target.value})}
                className="input-field"
              >
                <option value="">Todos</option>
                <option value="true">Público</option>
                <option value="false">Privado</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Documentos */}
      <div className="card">
        <div className="card-body">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Documento
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Categoria
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tamanho
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Downloads
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Visibilidade
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {documentosData?.documentos?.map((documento) => (
                  <tr key={documento.documento_id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="text-2xl mr-3">
                          {getTipoIcon(documento.documento_tipo)}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {documento.documento_titulo}
                          </div>
                          <div className="text-sm text-gray-500">
                            {documento.documento_descricao || 'Sem descrição'}
                          </div>
                          <div className="flex items-center text-xs text-gray-400">
                            {getFormatoIcon(documento.documento_formato)}
                            <span className="ml-1 uppercase">{documento.documento_formato}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="capitalize text-sm text-gray-900">
                        {documento.documento_tipo}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="capitalize text-sm text-gray-900">
                        {documento.documento_categoria}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatFileSize(documento.documento_tamanho || 0)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {documento.documento_downloads || 0}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        documento.documento_publico 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {documento.documento_publico ? 'Público' : 'Privado'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleDownload(documento.documento_id)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Download"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        {isAdministrador() && (
                          <>
                            <button
                              onClick={() => handleEdit(documento)}
                              className="text-green-600 hover:text-green-900"
                              title="Editar"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(documento.documento_id)}
                              className="text-red-600 hover:text-red-900"
                              title="Excluir"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal de Cadastro/Edição */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-container custom-scrollbar">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingDocumento ? 'Editar Documento' : 'Adicionar Documento'}
                </h3>
                <button
                  onClick={resetForm}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Título *
                    </label>
                    <input
                      type="text"
                      name="documento_titulo"
                      defaultValue={editingDocumento?.documento_titulo || ''}
                      required
                      className="input-field"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo *
                    </label>
                    <select
                      name="documento_tipo"
                      defaultValue={editingDocumento?.documento_tipo || ''}
                      required
                      className="input-field"
                    >
                      <option value="">Selecione</option>
                      <option value="regulamento">Regulamento</option>
                      <option value="ata">Ata</option>
                      <option value="contrato">Contrato</option>
                      <option value="manual">Manual</option>
                      <option value="formulario">Formulário</option>
                      <option value="outros">Outros</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Categoria *
                    </label>
                    <select
                      name="documento_categoria"
                      defaultValue={editingDocumento?.documento_categoria || ''}
                      required
                      className="input-field"
                    >
                      <option value="">Selecione</option>
                      <option value="administrativo">Administrativo</option>
                      <option value="financeiro">Financeiro</option>
                      <option value="juridico">Jurídico</option>
                      <option value="tecnico">Técnico</option>
                      <option value="social">Social</option>
                      <option value="outros">Outros</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Versão
                    </label>
                    <input
                      type="text"
                      name="documento_versao"
                      defaultValue={editingDocumento?.documento_versao || ''}
                      className="input-field"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data do Documento
                    </label>
                    <input
                      type="date"
                      name="documento_data_documento"
                      defaultValue={editingDocumento?.documento_data_documento || ''}
                      className="input-field"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Validade
                    </label>
                    <input
                      type="date"
                      name="documento_data_validade"
                      defaultValue={editingDocumento?.documento_data_validade || ''}
                      className="input-field"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição
                  </label>
                  <textarea
                    name="documento_descricao"
                    defaultValue={editingDocumento?.documento_descricao || ''}
                    rows={3}
                    className="input-field"
                  />
                </div>
                
                {!editingDocumento && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Arquivo *
                    </label>
                    <input
                      type="file"
                      name="arquivo"
                      required
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                      className="input-field"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Formatos aceitos: PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG, PNG (máx. 10MB)
                    </p>
                  </div>
                )}
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="documento_publico"
                    defaultChecked={editingDocumento?.documento_publico !== false}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Documento público (visível para todos)</span>
                </div>
                
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="btn-secondary"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={documentoMutation.isLoading}
                    className="btn-primary"
                  >
                    {documentoMutation.isLoading ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documentos;
